/**
 * A tabela de `/equipe/alocacao` — o que a página desenha, montado do payload.
 *
 * Espelha server/lib/equipe/alocacao-relatorio.ts (só os tipos e os ids
 * sintéticos). NENHUM dia é somado aqui: célula, coluna Fazenda (total da linha)
 * e linha Total saem prontos do servidor, do mesmo `creditar()`. O client só
 * escolhe QUAIS linhas e colunas mostrar, divide por área no "por ha" e formata.
 * Um segundo caminho de soma no client é como os dois divergem depois.
 *
 * Puro (sem React): é o que deixa a montagem e o CSV testáveis sem DOM
 * (`alocacao-tabela.test.ts`).
 */

// ─── Payload (espelho do servidor) ───────────────────────────────────────────

export const SEM_ATIVIDADE = "SEM_ATIVIDADE";
/** Tarefa sem setor. É o único id não-uuid que o filtro `setorIds` aceita. */
export const SEM_SETOR = "SEM_SETOR";
/** Setor que não existe mais. Coluna visível, mas fora do filtro (a API recusa o id). */
export const SETOR_REMOVIDO = "SETOR_REMOVIDO";

export interface CelulaAlocacao {
  dias: number;
  tarefas: number;
}

export interface SetorDoRelatorio {
  id: string;
  nome: string;
  tipo: "PRODUTIVO" | "SERVICO" | null;
  areaHectares: number | null;
}

export interface RelatorioAlocacao {
  periodo: { dataInicio: string; dataFim: string };
  filtro: { setorIds: string[]; funcionarioIds: string[] };
  totais: { dias: number; tarefas: number; funcionarios: number; diasCalendario: number };
  atividades: Array<{ id: string; nome: string }>;
  setores: SetorDoRelatorio[];
  funcionarios: Array<{ id: string; nome: string }>;
  atividadeSetor: Array<{ atividadeId: string; setorId: string } & CelulaAlocacao>;
  funcionarioAtividade: Array<{ funcionarioId: string; atividadeId: string } & CelulaAlocacao>;
  funcionarioSetor: Array<{ funcionarioId: string; setorId: string } & CelulaAlocacao>;
  porAtividade: Array<{ id: string } & CelulaAlocacao>;
  porSetor: Array<{ id: string } & CelulaAlocacao>;
  porFuncionario: Array<{ id: string } & CelulaAlocacao>;
}

// ─── Tabela ──────────────────────────────────────────────────────────────────

export type VisaoAlocacao = "atividade-setor" | "funcionario-atividade" | "funcionario-setor";

export interface ColunaTabela {
  id: string;
  nome: string;
  /** Setor de serviço — cabeçalho em tom apagado. */
  servico: boolean;
  /** Área do setor. null em coluna de atividade, setor sem área e coluna sintética. */
  areaHectares: number | null;
  /** "Sem setor" / "Setor removido": não é cadastro que falta, é a ausência dele. */
  sintetica: boolean;
}

export interface LinhaTabela {
  id: string;
  nome: string;
  /** A coluna Fazenda: `porAtividade`/`porFuncionario` do servidor. */
  fazenda: CelulaAlocacao;
  /** Alinhadas com `colunas`. Célula ausente no payload = zero. */
  celulas: CelulaAlocacao[];
}

export interface TabelaAlocacao {
  visao: VisaoAlocacao;
  rotuloLinha: string;
  colunasSaoSetores: boolean;
  colunas: ColunaTabela[];
  linhas: LinhaTabela[];
  /** Linha Total: Fazenda = `totais`; colunas = `porSetor`/`porAtividade`. */
  total: { fazenda: CelulaAlocacao; celulas: CelulaAlocacao[] };
}

const VAZIA: CelulaAlocacao = { dias: 0, tarefas: 0 };

function celula(c: CelulaAlocacao | undefined): CelulaAlocacao {
  return c ? { dias: c.dias, tarefas: c.tarefas } : VAZIA;
}

function indexar<T extends { id: string }>(lista: T[]): Map<string, T> {
  const mapa = new Map<string, T>();
  lista.forEach(x => mapa.set(x.id, x));
  return mapa;
}

function matriz<T extends CelulaAlocacao>(
  lista: T[],
  linha: (x: T) => string,
  coluna: (x: T) => string,
): (l: string, c: string) => CelulaAlocacao {
  const mapa = new Map<string, T>();
  lista.forEach(x => mapa.set(`${linha(x)}|${coluna(x)}`, x));
  return (l, c) => celula(mapa.get(`${l}|${c}`));
}

/**
 * Linhas e colunas da visão ativa.
 *
 * Colunas de SETOR: o universo do período, na ordem do servidor (produtivos,
 * serviço, sintéticos). Com filtro de setor, só os selecionados — mesmo um sem
 * dia no recorte, porque escolher A3 e não ver a coluna A3 seria pior que vê-la
 * vazia. Sem filtro, só os que receberam alguma tarefa no recorte.
 *
 * Colunas de ATIVIDADE e as linhas: a ordem de `porAtividade`/`porFuncionario`,
 * que o servidor já entrega por dias desc no recorte filtrado.
 */
export function montarTabela(r: RelatorioAlocacao, visao: VisaoAlocacao): TabelaAlocacao {
  const nomeAtividade = indexar(r.atividades);
  const nomeFuncionario = indexar(r.funcionarios);
  const porAtividade = indexar(r.porAtividade);
  const porSetor = indexar(r.porSetor);
  const totalFazenda = { dias: r.totais.dias, tarefas: r.totais.tarefas };

  const filtroSetor = new Set(r.filtro.setorIds);
  const colunasSetor: ColunaTabela[] = r.setores
    .filter(s => (filtroSetor.size > 0 ? filtroSetor.has(s.id) : porSetor.has(s.id)))
    .map(s => ({
      id: s.id,
      nome: s.nome,
      servico: s.tipo === "SERVICO",
      areaHectares: s.areaHectares,
      sintetica: s.id === SEM_SETOR || s.id === SETOR_REMOVIDO,
    }));

  if (visao === "atividade-setor") {
    const valor = matriz(r.atividadeSetor, c => c.atividadeId, c => c.setorId);
    return {
      visao,
      rotuloLinha: "Atividade",
      colunasSaoSetores: true,
      colunas: colunasSetor,
      linhas: r.porAtividade.map(a => ({
        id: a.id,
        nome: nomeAtividade.get(a.id)?.nome ?? "—",
        fazenda: celula(a),
        celulas: colunasSetor.map(c => valor(a.id, c.id)),
      })),
      total: { fazenda: totalFazenda, celulas: colunasSetor.map(c => celula(porSetor.get(c.id))) },
    };
  }

  if (visao === "funcionario-atividade") {
    const valor = matriz(r.funcionarioAtividade, c => c.funcionarioId, c => c.atividadeId);
    const colunas: ColunaTabela[] = r.porAtividade.map(a => ({
      id: a.id,
      nome: nomeAtividade.get(a.id)?.nome ?? "—",
      servico: false,
      areaHectares: null,
      sintetica: a.id === SEM_ATIVIDADE,
    }));
    return {
      visao,
      rotuloLinha: "Funcionário",
      colunasSaoSetores: false,
      colunas,
      linhas: r.porFuncionario.map(f => ({
        id: f.id,
        nome: nomeFuncionario.get(f.id)?.nome ?? "—",
        fazenda: celula(f),
        celulas: colunas.map(c => valor(f.id, c.id)),
      })),
      total: { fazenda: totalFazenda, celulas: colunas.map(c => celula(porAtividade.get(c.id))) },
    };
  }

  const valor = matriz(r.funcionarioSetor, c => c.funcionarioId, c => c.setorId);
  return {
    visao,
    rotuloLinha: "Funcionário",
    colunasSaoSetores: true,
    colunas: colunasSetor,
    linhas: r.porFuncionario.map(f => ({
      id: f.id,
      nome: nomeFuncionario.get(f.id)?.nome ?? "—",
      fazenda: celula(f),
      celulas: colunasSetor.map(c => valor(f.id, c.id)),
    })),
    total: { fazenda: totalFazenda, celulas: colunasSetor.map(c => celula(porSetor.get(c.id))) },
  };
}

// ─── Por hectare ─────────────────────────────────────────────────────────────

function areaUtil(area: number | null): number | null {
  return area !== null && Number.isFinite(area) && area > 0 ? area : null;
}

/** Divisor da coluna Fazenda no "por ha": Σ das áreas das colunas exibidas que TÊM área. */
export function areaDasColunas(colunas: ColunaTabela[]): number {
  return colunas.reduce((soma, c) => soma + (areaUtil(c.areaHectares) ?? 0), 0);
}

/** Setores de verdade sem área cadastrada (o rodapé do "por ha"). Sintéticos não contam. */
export function setoresSemArea(colunas: ColunaTabela[]): number {
  return colunas.filter(c => !c.sintetica && areaUtil(c.areaHectares) === null).length;
}

/** Dias ÷ área. `null` = sem área — a tela mostra "—", nunca um zero que não é zero. */
export function porHectare(dias: number, area: number | null): number | null {
  const a = areaUtil(area);
  return a === null ? null : dias / a;
}

// ─── Formato ─────────────────────────────────────────────────────────────────

const UMA_CASA = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const AREA = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 2 });

/** "11,5" — dias e diárias/ha na tela. */
export function formatarDias(valor: number): string {
  return UMA_CASA.format(valor);
}

export function formatarArea(hectares: number): string {
  return AREA.format(hectares);
}

// ─── CSV ─────────────────────────────────────────────────────────────────────

/**
 * Número para planilha PT-BR: vírgula decimal, sem separador de milhar. Dias vão
 * com as 3 casas do servidor (o milésimo é exato), para as células somarem a
 * coluna Fazenda na planilha também; diárias/ha, com 2.
 */
function numeroCsv(valor: number, casas: number): string {
  const fator = Math.pow(10, casas);
  const arredondado = Math.round(valor * fator) / fator;
  return String(arredondado).replace(".", ",");
}

function textoCsv(texto: string): string {
  return /[;"\n\r]/.test(texto) ? `"${texto.replace(/"/g, '""')}"` : texto;
}

/**
 * Valor de uma célula como a tela mostra: dias, ou dias ÷ área no "por ha".
 * `null` só no "por ha", quando não há área para dividir.
 */
export function valorExibido(dias: number, area: number | null, porHa: boolean): number | null {
  return porHa ? porHectare(dias, area) : dias;
}

function linhasDaTabela(t: TabelaAlocacao, porHa: boolean): Array<{ rotulo: string; fazenda: number | null; celulas: Array<number | null> }> {
  const areaFazenda = areaDasColunas(t.colunas);
  const montar = (rotulo: string, fazenda: CelulaAlocacao, celulas: CelulaAlocacao[]) => ({
    rotulo,
    fazenda: valorExibido(fazenda.dias, areaFazenda, porHa),
    celulas: celulas.map((c, i) => valorExibido(c.dias, t.colunas[i].areaHectares, porHa)),
  });
  return t.linhas
    .map(l => montar(l.nome, l.fazenda, l.celulas))
    .concat([montar("Total", t.total.fazenda, t.total.celulas)]);
}

/**
 * CSV da visão ativa: linha, Fazenda, colunas e a linha Total, separador `;` e
 * vírgula decimal (abre direto no Excel/Numbers PT-BR). Sem área no "por ha" =
 * célula vazia, não zero.
 */
export function tabelaParaCsv(t: TabelaAlocacao, porHa: boolean): string {
  const casas = porHa ? 2 : 3;
  const num = (v: number | null) => (v === null ? "" : numeroCsv(v, casas));
  const cabecalho = [t.rotuloLinha, "Fazenda"].concat(t.colunas.map(c => c.nome)).map(textoCsv).join(";");
  const corpo = linhasDaTabela(t, porHa).map(l =>
    [textoCsv(l.rotulo), num(l.fazenda)].concat(l.celulas.map(num)).join(";"));
  return [cabecalho].concat(corpo).join("\n");
}

function escaparHtml(texto: string): string {
  return texto.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

/**
 * A MESMA tabela em HTML, para a área de transferência. Colar `;` numa planilha
 * joga tudo numa coluna só (Excel, Sheets e Numbers separam colagem por TAB ou
 * por tabela HTML, não por `;`); com o HTML junto, a planilha recebe as colunas
 * certas e um editor de texto continua recebendo o CSV.
 */
export function tabelaParaHtml(t: TabelaAlocacao, porHa: boolean): string {
  const casas = porHa ? 2 : 3;
  const num = (v: number | null) => (v === null ? "" : numeroCsv(v, casas));
  const linha = (celulas: string[], tag: "th" | "td") =>
    `<tr>${celulas.map(c => `<${tag}>${escaparHtml(c)}</${tag}>`).join("")}</tr>`;
  const cabecalho = linha([t.rotuloLinha, "Fazenda"].concat(t.colunas.map(c => c.nome)), "th");
  const corpo = linhasDaTabela(t, porHa)
    .map(l => linha([l.rotulo, num(l.fazenda)].concat(l.celulas.map(num)), "td"))
    .join("");
  return `<table>${cabecalho}${corpo}</table>`;
}
